// src/contexts/AuthContext.tsx

import React, { createContext, useContext, useState, useEffect } from 'react';

interface User {
  id: string;
  name: string;
  first_name: string; // Keep both for compatibility
  last_name?: string;
  email: string;
  role: 'customer' | 'trainer' | 'admin'; 
  username: string;
  profile_picture?: string;
  bio?: string;
  fitness_goals?: string;
  phone_number: string;
  // Add other user properties as needed
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName: string, phoneNumber: string, username: string, tosAccepted?: boolean,
    privacyAccepted?: boolean, lastName?: string, role?: 'customer' | 'trainer' | 'admin') => Promise<void>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void; // Add this function
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  // API base URL for Django backend
  const API_BASE_URL = 'https://aztrainersbackend.onrender.com';

  useEffect(() => {
    // Check if user is already logged in (from localStorage, etc.)
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
  try {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('authToken');
    
    if (savedUser && savedToken) {
      // Optionally verify the token is still valid by making a test API call
      const userData = JSON.parse(savedUser);
      setUser(userData);
      setIsAuthenticated(true);
    }
  } catch (error) {
    console.error('Auth check failed:', error);
    // Clear invalid data
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
  } finally {
    setLoading(false);
  }
};

  // Add updateUser function
  const updateUser = (updates: Partial<User>) => {
    if (!user) return;
    
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    console.log('User updated in AuthContext:', updatedUser);



    
  };

  const login = async (email: string, password: string) => {
  try {
    console.log('Attempting login for:', email);
    
    const response = await fetch(`${API_BASE_URL}/api/login/`, {
      method: 'POST',
      credentials: 'include', // Important for any remaining cookie attempts
      headers: { 
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ 
        email: email,        // Changed from username to email
        password: password 
      }),
    });

    const responseData = await response.json();
    console.log('Response data:', responseData);
    
    if (response.ok && responseData.success) {
      console.log('Login successful:', responseData);
      
      // Store the token in localStorage
      if (responseData.token) {
        localStorage.setItem('authToken', responseData.token);
        console.log('Stored auth token:', responseData.token);
      }
      
      const user = {
        id: responseData.user_id,
        username: responseData.username,
        first_name: responseData.first_name || responseData.username,
        last_name: responseData.last_name || '',
        name: responseData.first_name ? `${responseData.first_name} ${responseData.last_name || ''}`.trim() : responseData.username,
        email: responseData.email || '',
        phone_number: responseData.phone_number || '',
        role: responseData.user_type,
        profile_picture: responseData.profile_picture,
        bio: responseData.bio,              
        fitness_goals: responseData.fitness_goals, 
      };
      
      setUser(user);
      setIsAuthenticated(true);
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      throw new Error(responseData.error || 'Login failed');
    }
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

  const register = async ( email: string, password: string, firstName: string, phoneNumber: string, username: string, tosAccepted: boolean = false,
  privacyAccepted: boolean = false, lastName?: string, role: 'customer' | 'trainer' | 'admin' = 'customer') => {
    try {
      console.log('Attempting registration for:', email);
      
      const response = await fetch(`${API_BASE_URL}/api/register/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          username: username,
          email, 
          password, 
          first_name: firstName,
          last_name: lastName,
          user_type: role,
          phone_number: phoneNumber,
          tos_accepted: tosAccepted,
          privacy_accepted: privacyAccepted
        }),
      });

      if (response.ok) {
        const userData = await response.json();
        console.log('Registration successful:', userData);

        if (userData.token) {
        localStorage.setItem('authToken', userData.token);
      }
        
        // Transform the response to match your User interface
        const user = {
          id: userData.id || userData.user_id,
          username: userData.username,
          first_name: userData.first_name,
          last_name: userData.last_name,
          name: `${userData.first_name} ${userData.last_name}`.trim(),
          email: userData.email,
          phone_number: userData.phone_number,
          role: userData.user_type || userData.role
        };
        
        setUser(user);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = () => {
  console.log('Logging out user');
  setUser(null);
  setIsAuthenticated(false);
  localStorage.removeItem('user');
  localStorage.removeItem('authToken'); // Clear the token
  
  // Force a page refresh to clear any cached state
  window.location.reload();
};

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      loading,
      login,
      register,
      logout,
      updateUser, // Add this to the provider
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}